import { useState, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Cartao, Conteudo, Rotulo, ValorDestacado } from "../styles";
import { IconFecharCircunferencia, IconClock } from "../../../icons/IconesSistema";
import { IconArrowDown, IconArrowUp } from "../../../icons/index";
import { IconUser } from "../../../icons";
import { useAuth } from "../../../../contexts/UserContext";
import { 
  ContagemRecusas, 
  ParagrafoSemRecusas, 
  TituloRecusas, 
  CardRecusa,
  HeaderRecusa,
  AvatarContainer,
  InfoContainer,
  NomeContainer,
  NomeProfissional,
  CodigoProfissional,
  DataRelativa,
  BotaoExpandir
} from "./styles";
import { comum } from "../../../../styles/Themes/light";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const formatarDataRelativa = (data: string | Date | null | undefined): string => {
  if (!data) return "";
  try {
    const dataRecusa = new Date(data);
    if (Number.isNaN(dataRecusa.getTime())) return "";
    return formatDistanceToNow(dataRecusa, { 
      addSuffix: true, 
      locale: ptBR 
    });
  } catch (error) {
    return "";
  }
};

export default function RecusasProfissionais({ demanda, loading = false }: any) {
  const { user } = useAuth();
  
  const recusas = useMemo(
    () => (demanda?.recusas_profissionais as any[]) ?? [],
    [demanda?.recusas_profissionais]
  );

  const [aberto, setAberto] = useState(false);
  const alternar = () => setAberto((visivel) => !visivel);

  const podeVerRecusas = user?.profile === 'admin' || user?.profile === 'operador';
  
  if (!podeVerRecusas) {
    return null;
  }

  // ERRO26: Não exibir seção se não houver recusas
  if (recusas.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <Cartao>
        <TituloRecusas $semRecusas={false}>
          <Skeleton circle width={18} height={18} />
          <Skeleton width={180} height={18} />
          <Skeleton width={24} height={24} borderRadius={12} />
          <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
            <Skeleton width={18} height={18} />
          </span>
        </TituloRecusas>
      </Cartao>
    );
  }

  return (
    <Cartao>
      <TituloRecusas $semRecusas={recusas.length === 0}>
        <IconFecharCircunferencia size={18} color="#E52940" />
        Recusas de Profissionais
        <ContagemRecusas>{recusas.length}</ContagemRecusas>
        <BotaoExpandir onClick={alternar}>
          {aberto ? <IconArrowUp /> : <IconArrowDown />}
        </BotaoExpandir>
      </TituloRecusas>

      {aberto && (
        <Conteudo>
          {recusas.length === 0 ? (
            <ParagrafoSemRecusas>Sem recusas registradas</ParagrafoSemRecusas>
          ) : (
            recusas.map((recusa) => {
              const idUsuario = recusa.id_usuario;

              const nomeCompleto = (
                recusa.nome_profissional ||
                recusa.usuario?.nome ||
                ""
              ).trim();

              const primeiroNome = nomeCompleto ? nomeCompleto.split(' ')[0] : "";

              const codigoFormatado = idUsuario ? `D${idUsuario}` : "";

              const dataRelativa = formatarDataRelativa(recusa.criado || recusa.modificado);

              return (
                <CardRecusa key={recusa.id_recusa_profissional}>
                  <HeaderRecusa>
                    <AvatarContainer>
                      <IconUser color={comum.colors.primaryRed400} size={20} />
                    </AvatarContainer>
                    <InfoContainer>
                      <NomeContainer>
                        {primeiroNome ? (
                          <>
                            <NomeProfissional>{primeiroNome}</NomeProfissional>
                            {codigoFormatado && <CodigoProfissional>{codigoFormatado}</CodigoProfissional>}
                          </>
                        ) : codigoFormatado ? (
                          <NomeProfissional>{codigoFormatado}</NomeProfissional>
                        ) : (
                          <NomeProfissional>Profissional não identificado</NomeProfissional>
                        )}
                        {dataRelativa && (
                          <DataRelativa>
                            <IconClock size={14} />
                            {dataRelativa}
                          </DataRelativa>
                        )}
                      </NomeContainer>
                    </InfoContainer>
                  </HeaderRecusa>
                  <Rotulo>Motivo da recusa:</Rotulo>
                  <ValorDestacado>{recusa.justificativa || "--"}</ValorDestacado>
                </CardRecusa>
              );
            })
          )}
        </Conteudo>
      )}
    </Cartao>
  );
}
